import { Injectable, signal } from '@angular/core';

export type ConfirmPopupType = 'info' | 'warning' | 'success' | 'error';

export interface ConfirmPopupConfig {
  message: string;
  type?: ConfirmPopupType;
  acceptLabel?: string;
  rejectLabel?: string;
  handleSaving?: boolean;
  showAcceptButton?: boolean;
  showRejectButton?: boolean;
}

export interface ConfirmPopupState {
  isActive: boolean;
  message: string;
  type: ConfirmPopupType;
  acceptLabel: string;
  rejectLabel: string;
  target: EventTarget | null;
  handleSaving: boolean;
  showAcceptButton: boolean;
  showRejectButton: boolean;
}

/** Contract the confirm-popup component fulfils so the service can drive it. */
export interface ConfirmPopupHost {
  open(): void;
  hide(): void;
}

@Injectable({
  providedIn: 'root',
})
export class ConfirmPopupService {
  // Signal-based state management
  private readonly confirmPopupState = signal<ConfirmPopupState>({
    isActive: false,
    message: '',
    type: 'warning',
    acceptLabel: 'YES',
    rejectLabel: 'NO',
    target: null,
    handleSaving: false,
    showAcceptButton: true,
    showRejectButton: true,
  });

  readonly isSaving = signal<boolean>(false);

  // Public readonly signal for components to subscribe to
  readonly currentConfirmation = this.confirmPopupState.asReadonly();

  private currentResolve: ((value: boolean) => void) | null = null;
  private scrollListener: (() => void) | null = null;
  private popup: ConfirmPopupHost | null = null;

  registerPopup(popup: ConfirmPopupHost): void {
    this.popup = popup;
  }

  unregisterPopup(popup: ConfirmPopupHost): void {
    if (this.popup === popup) {
      this.popup = null;
    }
  }

  confirm(target: EventTarget, config: ConfirmPopupConfig): Promise<boolean> {
    return new Promise((resolve) => {
      // If there's a previous unresolved popup, reject it first
      if (this.currentResolve) {
        this.currentResolve(false);
        this.currentResolve = null;
      }

      // Store the resolve function for later use
      this.currentResolve = resolve;

      // Close any existing confirmation first
      if (this.confirmPopupState().isActive) {
        this.popup?.hide();
        this.cleanup();
      }

      // Small delay to ensure clean state
      setTimeout(() => {
        const type = config.type || 'warning';

        // Update the signal state
        this.confirmPopupState.set({
          isActive: true,
          message: config.message,
          type,
          acceptLabel: config.acceptLabel || 'YES',
          rejectLabel: config.rejectLabel || 'NO',
          target,
          handleSaving: config.handleSaving || false,
          showAcceptButton: config.showAcceptButton ?? true,
          showRejectButton: config.showRejectButton ?? true,
        });

        this.setupScrollListener();

        // Trigger the registered popup (positions itself on the target)
        this.popup?.open();
      }, 200);
    });
  }

  private cleanup(): void {
    this.confirmPopupState.update((state) => ({ ...state, isActive: false }));
    this.removeScrollListener();
  }

  private setupScrollListener(): void {
    // Remove any existing listener first
    this.removeScrollListener();

    // Create the scroll handler
    this.scrollListener = () => {
      this.closeAll();
    };

    // Add listener to window for all scroll events (including nested scrollable elements)
    window.addEventListener('scroll', this.scrollListener, true);
  }

  private removeScrollListener(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
      this.scrollListener = null;
    }
  }

  // Public method to close all confirm popups
  closeAll(): void {
    if (this.confirmPopupState().isActive) {
      this.popup?.hide();
      this.cleanup();

      // Reject any pending promise
      if (this.currentResolve) {
        this.currentResolve(false);
        this.currentResolve = null;
      }
    }
  }

  acceptClick(): void {
    // Custom accept handler that doesn't close the popup
    if (this.currentResolve) {
      this.currentResolve(true);
      this.currentResolve = null;
    }
  }

  /** Accept resolved by the popup itself (non-handleSaving path). */
  notifyAccept(): void {
    this.confirmPopupState.update((state) => ({ ...state, isActive: false }));
    this.removeScrollListener();
    if (this.currentResolve) {
      this.currentResolve(true);
      this.currentResolve = null;
    }
  }

  /** Reject resolved by the popup itself (reject button / Escape). */
  notifyReject(): void {
    this.confirmPopupState.update((state) => ({ ...state, isActive: false }));
    this.removeScrollListener();
    if (this.currentResolve) {
      this.currentResolve(false);
      this.currentResolve = null;
    }
  }

  getIconName(type: ConfirmPopupType): string {
    const iconMap: Record<ConfirmPopupType, string> = {
      info: 'tablerInfoCircle',
      warning: 'tablerAlertTriangle',
      success: 'tablerCircleCheck',
      error: 'tablerCircleX',
    };
    return iconMap[type];
  }

  getStyleClass(type: ConfirmPopupType): string {
    return `confirm-popup-${type}-message`;
  }
}
